/**
 * B4 · Legal documents.
 *
 * Structured DRAFT copy — `draft: true` surfaces a "pending counsel review"
 * banner. These pages must exist + be reachable before launch (launch gate);
 * final wording must be reviewed by counsel. Copy intentionally avoids the
 * brand FORBIDDEN_WORDS — neutral legal language only.
 */

export interface LegalSection {
  heading: string;
  body: string;
}

export interface LegalDoc {
  slug: string;
  title: string;
  /** ISO date this draft was last touched. */
  updated: string;
  /** When true, the UI shows a "draft — pending counsel" banner. */
  draft: boolean;
  sections: LegalSection[];
}

const COMPANY = 'HOTTR PTY LTD';
const JURISDICTION = 'Victoria, Australia';
const ABUSE_EMAIL = 'safety@hottr.app';

export const LEGAL_DOCS: Record<string, LegalDoc> = {
  privacy: {
    slug: 'privacy',
    title: 'Privacy Policy',
    updated: '2026-05-30',
    draft: true,
    sections: [
      { heading: 'Who we are', body: `${COMPANY} operates HOTTR, an 18+ platform. This policy explains what we collect, why, and your choices. Governed by the laws of ${JURISDICTION}.` },
      { heading: 'What we collect', body: 'Account details (email), profile information you provide, photos you upload, approximate location, messages, age- and identity-verification status, device/usage signals, and payment identifiers (never full card numbers — those stay with the payment processor).' },
      { heading: 'How we use it', body: 'To run the core experience (discovery, presence, messaging), keep the platform safe (moderation, verification, anti-abuse), process subscriptions, and improve the product. We do not sell your personal information.' },
      { heading: 'Location', body: 'Distance is shown to others only as a fuzzed/approximate value — your exact coordinates are never shared with other users.' },
      { heading: 'Retention & deletion', body: 'We keep data only as long as needed for the purpose collected, then delete or anonymise it on a defined schedule. You can request deletion of your account and associated data.' },
      { heading: 'Your rights', body: `You may access, correct, or delete your data, and contact us about privacy at ${ABUSE_EMAIL}. Where required, we honour applicable privacy-law rights.` },
    ],
  },
  terms: {
    slug: 'terms',
    title: 'Terms of Service',
    updated: '2026-05-30',
    draft: true,
    sections: [
      { heading: 'Eligibility', body: 'You must be 18 or older to create an account or use HOTTR. Age verification is required; providing false age information terminates your access.' },
      { heading: 'Your account', body: 'You are responsible for your account and the content you post. Keep your credentials secure.' },
      { heading: 'Acceptable use', body: 'Use of HOTTR is subject to our Acceptable Use Policy. Prohibited content and conduct lead to removal and possible permanent ban.' },
      { heading: 'Subscriptions', body: 'Paid tiers renew until cancelled. Pricing and billing are shown at checkout. See the Refund Policy.' },
      { heading: 'Disclaimers & liability', body: 'HOTTR is provided "as is". To the extent permitted by law, our liability is limited. Nothing here excludes rights you have under the Australian Consumer Law.' },
      { heading: 'Governing law', body: `These terms are governed by the laws of ${JURISDICTION}.` },
    ],
  },
  'acceptable-use': {
    slug: 'acceptable-use',
    title: 'Acceptable Use Policy',
    updated: '2026-05-30',
    draft: true,
    sections: [
      { heading: 'Zero tolerance', body: 'Child sexual abuse material (CSAM) and any sexualisation of minors are strictly prohibited. We scan uploads, remove violations, preserve evidence, and report to the relevant authorities as required by law.' },
      { heading: 'Consent & legality', body: 'All content must involve consenting adults (18+). No non-consensual content, no content that is illegal where you or the subject are located.' },
      { heading: 'Prohibited conduct', body: 'No harassment, threats, doxxing, impersonation, spam, solicitation that the platform does not support, or attempts to evade moderation, bans, or location controls.' },
      { heading: 'Enforcement', body: 'We may quarantine media pending review, remove content, warn, throttle, suspend, or permanently ban accounts. Serious violations are escalated immediately.' },
    ],
  },
  refund: {
    slug: 'refund',
    title: 'Refund Policy',
    updated: '2026-05-30',
    draft: true,
    sections: [
      { heading: 'Subscriptions', body: 'Subscriptions are billed in advance per period. You can cancel anytime; cancellation stops future renewals and access continues to the end of the paid period.' },
      { heading: 'Refunds', body: 'Refunds are handled case-by-case and in line with the Australian Consumer Law and the payment processor’s rules. Contact support to request one.' },
      { heading: 'Chargebacks', body: 'If you have an issue, contact us first — chargebacks raised without contacting support may result in account suspension.' },
    ],
  },
  contact: {
    slug: 'contact',
    title: 'Safety & Contact',
    updated: '2026-05-30',
    draft: true,
    sections: [
      { heading: 'Report abuse', body: `To report content or behaviour that breaks our rules, use the in-app report tools or email ${ABUSE_EMAIL}. Urgent CSAM reports are actioned immediately.` },
      { heading: 'Takedown requests', body: `Rights holders and individuals seeking removal of content can contact ${ABUSE_EMAIL} with details.` },
      { heading: 'General support', body: `For account, billing, or verification help, contact ${ABUSE_EMAIL}.` },
    ],
  },
};

export const LEGAL_SLUGS = Object.keys(LEGAL_DOCS);

/** Look up a legal doc by slug. Unknown / missing slug → null (fail safe). */
export const getLegalDoc = (slug: string | null | undefined): LegalDoc | null => {
  if (!slug) return null;
  return LEGAL_DOCS[slug] ?? null;
};
